import { useEffect, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { useMedia } from 'react-use'
import Link from 'next/link'

import Burger from '@/components/base/burger'
import Logo from '@/components/base/logo'
import Menu from '@/components/layout/menu'
import { useLenis, useLenisStore } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'
import { useTheme } from '@/contexts/theme'
import { useTransitionStore } from '@/contexts/transition'
import { useLayoutStore } from '@/store/layout'

import Typography from '../base/typography'
import { cn } from '@/lib/utils'

function Header() {
  const { menu } = useStore()
  const { lenis } = useLenis()
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuOpen, headerHide, headerBackground] = useLayoutStore((state) => [
    state.menuOpen,
    state.headerHide,
    state.headerBackground
  ])
  const setLayoutState = useLayoutStore((state) => state.setState)
  const isMobile = useMedia('(max-width: 767px)', false)

  const { theme } = useTheme()

  useEffect(() => {
    if (!lenis) return
    if (menuOpen) {
      lenis.stop()
    } else {
      lenis.start()
    }
  }, [menuOpen, lenis])

  const lastScrollRef = useRef<number | null>(null)

  useEffect(() => {
    const unsubscribe = useTransitionStore.subscribe(
      (state) => state.reveal,
      (reveal) => {
        if (!reveal) return
        setLayoutState({ menuOpen: false, headerHide: false, headerBackground: false })
      }
    )

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  useEffect(() => {
    const unsubscribe = useLenisStore.subscribe(
      (state) => [state.scroll, state.direction],
      ([scroll, direction]) => {
        const currentScroll = scroll ?? 0
        const activeScroll = currentScroll > 200

        // Aggiorna lo sfondo dell'header in base allo scroll
        if (activeScroll) {
          if (direction === 1) {
            // Scrolling verso il basso: applica un threshold di 20px
            if (lastScrollRef.current === null) {
              // Imposta il punto di riferimento se non è ancora definito
              lastScrollRef.current = currentScroll
            } else if (currentScroll - lastScrollRef.current >= 20) {
              setLayoutState({ headerHide: true })
              // Aggiorna il riferimento per futuri controlli
              lastScrollRef.current = currentScroll
            }
          } else {
            // Scrolling verso l'alto: mostra l'header solo se lo scroll verso l'alto supera i 20px
            if (lastScrollRef.current === null) {
              lastScrollRef.current = currentScroll
            } else if (lastScrollRef.current - currentScroll >= 20) {
              setLayoutState({ headerHide: false, headerBackground: true })
              lastScrollRef.current = currentScroll
            }
          }
        } else if (!useTransitionStore.getState().active) {
          // Se lo scroll non è "attivo", mostra l'header e resetta il riferimento
          setLayoutState({ headerHide: false, headerBackground: false })
          lastScrollRef.current = null
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  return (
    <header
      className={cn([
        { dark: theme?.header === 'dark' },
        'fixed left-0 right-0 top-0 z-header flex h-header-height items-center justify-between bg-transparent px-offset',
        'transition-all duration-500 ease-out-cubic',
        'pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto',
        'text-white-light dark:text-text-accent-light',
        {
          'bg-white-light !text-text-accent-light': headerBackground,
          '-translate-y-full': headerHide
        }
      ])}
    >
      <Link
        href="/"
        scroll={false}
        className={cn('relative z-20 transition-colors duration-500', {
          'text-text-accent-light': menuOpen && isMobile
        })}
      >
        <Logo />
      </Link>

      <nav className="hidden items-center lg:flex lg:space-x-[4.5rem]">
        {menu?.header_menu?.menu?.map(({ title, url, target }, index) => (
          <Link
            key={`menu-${index}`}
            href={url}
            target={target}
            className={cn([
              'inline-flex items-center gap-spacing-xs whitespace-nowrap text-center text-body'
            ])}
            data-motion-link-menu
            scroll={false}
          >
            <span className="size-[.6rem] rounded bg-current" />
            <Typography
              variant="large"
              className="underline-effect [&_b]:font-medium"
            >{`<b>${title}</b>`}</Typography>
          </Link>
        ))}
      </nav>
      <Burger onClick={() => setLayoutState({ menuOpen: !menuOpen })} />

      <Transition
        nodeRef={menuRef}
        in={menuOpen}
        timeout={{
          enter: 0,
          exit: 800
        }}
        unmountOnExit
      >
        {(state) => <Menu ref={menuRef} state={state} menuOpen={menuOpen} />}
      </Transition>
    </header>
  )
}

export default Header
